import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import {
  CatalogStage,
  CdnImage,
  CmsLink,
  CmsPage,
  InternalCmsLink,
  MarketingContent,
} from '@markant-platform/shared/content/model';
import { AppConfigService, LocalesArray } from '@markant-platform/shared/data-access';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  static readonly oneTrustAction = 'OneTrust.ToggleInfoDisplay()';
  apiUrl: string = AppConfigService.appConfiguration.apiUrl['contentService'];

  constructor(private http: HttpClient) {}

  /**
   * Get catalog stage from content service.
   * This function is used to get slider contents in cartridge catalog.
   *
   * @returns {Observable<CatalogStage>}
   */
  public getCatalogStage(): Observable<CatalogStage> {
    return this.http.get<CatalogStage>(`${this.apiUrl}/catalog-stage`);
  }

  /**
   * Get background image infos for cartridge catalog.
   *
   * @returns {Observable<CdnImage>}
   */
  public getCatalogStageImage(isDetail = false): Observable<CdnImage> {
    return this.http.get<CdnImage>(`${this.apiUrl}/catalog-stage-image?isDetail=${isDetail}`);
  }

  /**
   * Get footer links from cms.
   *
   * @returns {Observable<CmsLink>}
   */
  public getFooterLinks(isPublic: boolean): Observable<CmsLink[]> {
    return this.http.get<CmsLink[]>(`${this.apiUrl}/footer-links`, { params: { publicPage: isPublic } });
  }

  public getTermsOfUseLink(): Observable<CmsLink[]> {
    return this.http.get<CmsLink[]>(`${this.apiUrl}/terms-of-use`).pipe(
      catchError(_ => {
        const defaultLink: CmsLink = { href: '/termsofuse', target: '_blank', text: '', externalLink: false };
        return of([defaultLink]);
      }),
      map(links => links.map(link => ({ ...link, target: '_blank' })))
    );
  }

  public getInternalTermsOfUseLink(): Observable<InternalCmsLink[]> {
    return this.http
      .get<CmsLink[]>(`${this.apiUrl}/private/terms-of-use`)
      .pipe(map(links => links.map(this.mapCmsLinkToInternalLink)));
  }

  public getPrivacyPolicyLink(): Observable<CmsLink[]> {
    return this.http.get<CmsLink[]>(`${this.apiUrl}/privacy-notice`).pipe(
      catchError(_ => {
        const defaultLink: CmsLink = { href: '/privacy-notice', target: '_blank', text: '', externalLink: false };
        return of([defaultLink]);
      }),
      map(links => links.map(link => ({ ...link, target: '_blank' })))
    );
  }

  public getServiceGTCsLink(isPublic: boolean): Observable<CmsLink[]> {
    return this.http.get<CmsLink[]>(`${this.apiUrl}/gtc`, { params: { publicPage: isPublic } }).pipe(
      catchError(_ => {
        const defaultLink: CmsLink = { href: '/gtc', target: '_blank', text: '', externalLink: false };
        return of([defaultLink]);
      }),
      map(links => links.map(link => ({ ...link, target: '_blank' })))
    );
  }

  public getInternalPrivacyPolicyLink(): Observable<InternalCmsLink[]> {
    return this.http
      .get<CmsLink[]>(`${this.apiUrl}/private/privacy-notice`)
      .pipe(map(links => links.map(this.mapCmsLinkToInternalLink)));
  }

  getCmsPageStatic(pageId: number): Observable<CmsPage> {
    return this.http.get<CmsPage>(`${this.apiUrl}/static-page/${pageId}`);
  }

  public mapCmsLinkToInternalLink(link: CmsLink): InternalCmsLink {
    if (link.href.includes(ContentService.oneTrustAction)) {
      return {
        ...link,
        key: link.text.toLowerCase().split(' ').join('-'),
        label: link.text,
        isCookieButton: true,
      };
    }

    const routerLink = link.href
      .split('/')
      .filter(item => item !== '' && !LocalesArray.map(locale => locale.shorthand).includes(item))[0];
    const routerLinkSplit = routerLink.split('?');
    const urlParams = new URLSearchParams(routerLinkSplit[1]);
    const pageId = urlParams.get('pageId');

    return {
      ...link,
      key: link.text.toLowerCase().split(' ').join('-'),
      label: link.text,
      routerLink: routerLinkSplit[0],
      pageId: pageId || undefined,
    };
  }

  getMarketingContentByServiceContentId(serviceContentId: string): Observable<MarketingContent> {
    return this.http
      .get<MarketingContent>(`${this.apiUrl}/marketing/${serviceContentId}`)
      .pipe(catchError(() => of({})));
  }
}
